import * as THREE from 'three'
import {
  createGSplatClipPacket,
  type GSplatClipPacket,
  type GSplatClipState,
  updateGSplatClipPacketMatrix,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import {
  GSPLAT_BATCH_INDEX_COUNT,
  GSPLAT_BATCH_VERTEX_COUNT,
  getGSplatBatchInstanceCount,
  setBatchedSplatQuadGeometry,
} from '@/plugins/gsplat/core/gsplatBatching'
import { compileGSplatWorkBufferModifier } from '@/plugins/gsplat/effects/workBufferModifier'
import type { GSplatDirector } from '@/plugins/gsplat/lod/GSplatDirector'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import type { Layer } from '@/plugins/gsplat/lod/Layer'
import type { LayerComposition } from '@/plugins/gsplat/lod/LayerComposition'
import { GaussianSplatMaterial } from '@/plugins/gsplat/materials/GaussianSplatMaterial'
import { getRendererViewportDrawingBufferSize } from '@/plugins/gsplat/utils/getRendererViewportSize'
import { getLoggerManager } from '@/shared/logging'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_ALWAYS,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  GSPLAT_WORKBUFFER_UPDATE_ONCE,
  type GSplatShaderEffectLike,
  type GSplatWorkBufferModifier,
  type GSplatWorkBufferUpdateMode,
} from '@/shared/types/gsplat'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'

const log = getLoggerManager().getLogger('gsplat:core')

/**
 * 共享帧状态（高斯 pass 和中心点 pass 共用）
 */
export interface UnifiedLODFrameState {
  /** GSplat Manager 引用 */
  manager: GSplatManager | null
  /** 纹理尺寸 */
  textureSize: number
  /** 当前可见 splat 数量 */
  visibleSplatCount: number
  /** 前向 splat geometry 的 batched draw instance 数量 */
  batchInstanceCount: number
  /** 视口宽度 */
  viewportWidth: number
  /** 视口高度 */
  viewportHeight: number
  /** 合并后的状态纹理（用于中心点选中/锁定/删除状态） */
  mergedStateTexture: THREE.DataTexture | null
  /** 是否有效 */
  valid: boolean
}

/**
 * Unified LOD 渲染 Mesh
 *
 * 专门用于渲染 LOD 模式下的 3D Gaussian Splatting 数据。
 * 不同于 GaussianSplatMesh（从 SplatData 创建本地纹理），
 * 这个类从 GSplatManager.workBuffer 读取预处理的纹理数据。
 *
 * 工作流程：
 * 1. 在 onBeforeRender 中调用 director.update() 更新 LOD 评估和排序
 * 2. 从 GSplatManager 获取 WorkBuffer 纹理
 * 3. 绑定纹理到材质的 uniforms
 * 4. 使用实例化渲染绘制所有可见的 Splats
 *
 * @remarks
 * 对应 PlayCanvas 中由 GSplatRenderer 处理的渲染逻辑，
 * 但适配 Three.js 的 Mesh-based 渲染架构。
 */
export class UnifiedLODMesh extends THREE.Mesh<
  THREE.InstancedBufferGeometry,
  GaussianSplatMaterial
> {
  /**
   * GSplat Director（负责 LOD 评估和排序调度）
   */
  private director: GSplatDirector

  /**
   * Layer Composition（管理相机和图层）
   */
  private composition: LayerComposition

  /**
   * 关联的 Layer
   */
  private layer: Layer

  /**
   * 包围盒（用于视锥剔除）
   */
  private _boundingBox: THREE.Box3

  /**
   * 是否已初始化
   */
  private _initialized = false

  /**
   * 上一帧的 textureSize（用于检测变化）
   */
  private _lastTextureSize = 0

  /** 临时变量：视口尺寸（避免每帧分配） */
  private _tempViewportSize = new THREE.Vector2()

  private static _loggedNaNPosition = false
  private static _loggedNaNBoundingSphere = false

  /** PlayCanvas 风格的最终渲染 shader effect；当前按 unified 共享材质作用域生效 */
  private _shaderEffect: GSplatShaderEffectLike | null = null

  /** PlayCanvas 风格 WorkBuffer modifier，仅作用于 unified LOD WorkBuffer 写入阶段。 */
  private _workBufferModifier: CompiledGSplatWorkBufferModifier | null = null

  /** 由 ClipService 编译的全局 polygon/Box 裁切状态。 */
  private _clipState: GSplatClipState | null = null
  private _clipPacketVersion = -1
  private readonly _clipPacketMatrix = new THREE.Matrix4().makeScale(
    Number.NaN,
    Number.NaN,
    Number.NaN
  )

  /** WorkBuffer 刷新模式。 */
  private _workBufferUpdateMode: GSplatWorkBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO

  /** 帧缓存：上次更新的帧 ID */
  private _lastFrameId = -1

  /** 帧缓存：上次是否已生成状态纹理 */
  private _lastFrameHasStateTexture = false

  /** 帧缓存：共享帧状态 */
  private _frameState: UnifiedLODFrameState = {
    manager: null,
    textureSize: 0,
    visibleSplatCount: 0,
    batchInstanceCount: 0,
    viewportWidth: 0,
    viewportHeight: 0,
    mergedStateTexture: null,
    valid: false,
  }

  /**
   * 创建 Unified LOD 渲染 Mesh
   *
   * @param director - GSplat Director
   * @param composition - Layer Composition
   * @param layer - 关联的 Layer
   * @param boundingBox - 包围盒
   */
  constructor(
    director: GSplatDirector,
    composition: LayerComposition,
    layer: Layer,
    boundingBox: THREE.Box3
  ) {
    // 创建几何体和材质
    const geometry = UnifiedLODMesh.createGeometry()
    const material = new GaussianSplatMaterial({
      depthWrite: false,
      depthTest: true,
      transparent: true,
    })

    super(geometry, material)

    this.director = director
    this.composition = composition
    this.layer = layer
    this._boundingBox = boundingBox.clone()

    // 设置几何体的包围盒（用于视锥剔除）
    this.geometry.boundingBox = this._boundingBox.clone()
    this.updateBoundingSphereFromBox()

    // 初始实例数为 0（等待 WorkBuffer 数据）
    this.geometry.instanceCount = 0

    // 设置为透明对象，确保正确的渲染顺序
    this.renderOrder = 1000

    // 禁用视锥剔除（LOD 系统会处理）
    this.frustumCulled = false
  }

  /**
   * 创建实例化渲染的四边形几何体
   */
  private static createGeometry(): THREE.InstancedBufferGeometry {
    const geometry = new THREE.InstancedBufferGeometry()

    setBatchedSplatQuadGeometry(geometry)

    // 初始实例数为 0
    geometry.instanceCount = 0

    // 调试：捕获 computeBoundingSphere 的 NaN 来源
    const originalCompute = geometry.computeBoundingSphere.bind(geometry)
    geometry.computeBoundingSphere = () => {
      const pos = geometry.getAttribute('position')
      let hasNaN = false
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i)
        const y = pos.getY(i)
        const z = pos.getZ(i)
        if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
          hasNaN = true
          if (!UnifiedLODMesh._loggedNaNPosition) {
            log.error(
              `[UnifiedLODMesh] position has NaN at i=${i} x=${x} y=${y} z=${z} (computeBoundingSphere)`
            )
            UnifiedLODMesh._loggedNaNPosition = true
          }
          break
        }
      }
      originalCompute()
      const bs = geometry.boundingSphere
      if (bs && !Number.isFinite(bs.radius) && !UnifiedLODMesh._loggedNaNBoundingSphere) {
        log.error(
          `[UnifiedLODMesh] computeBoundingSphere produced NaN radius. center=(${bs.center.x},${bs.center.y},${bs.center.z}) radius=${bs.radius}`
        )
        UnifiedLODMesh._loggedNaNBoundingSphere = true
      } else if (!hasNaN && !UnifiedLODMesh._loggedNaNBoundingSphere && bs) {
        // 标记已检查
        UnifiedLODMesh._loggedNaNBoundingSphere = true
      }
    }

    return geometry
  }

  /**
   * 从包围盒更新包围球
   */
  private updateBoundingSphereFromBox(): void {
    if (!this.geometry.boundingSphere) {
      this.geometry.boundingSphere = new THREE.Sphere()
    }
    const { min, max } = this._boundingBox
    const finite =
      Number.isFinite(min.x) &&
      Number.isFinite(min.y) &&
      Number.isFinite(min.z) &&
      Number.isFinite(max.x) &&
      Number.isFinite(max.y) &&
      Number.isFinite(max.z)
    if (!finite) {
      log.error(
        `[UnifiedLODMesh] boundingBox has non-finite values: min=(${min.x},${min.y},${min.z}) max=(${max.x},${max.y},${max.z})`
      )
      this.geometry.boundingSphere.center.set(0, 0, 0)
      this.geometry.boundingSphere.radius = 0
      return
    }
    this._boundingBox.getBoundingSphere(this.geometry.boundingSphere)
    if (!Number.isFinite(this.geometry.boundingSphere.radius)) {
      log.error(
        `[UnifiedLODMesh] boundingSphere radius is NaN after update. box min=(${min.x},${min.y},${min.z}) max=(${max.x},${max.y},${max.z})`
      )
      this.geometry.boundingSphere.radius = 0
    }
  }

  /**
   * 更新包围盒
   *
   * @param boundingBox - 新的包围盒
   */
  updateBoundingBox(boundingBox: THREE.Box3): void {
    this._boundingBox.copy(boundingBox)
    if (this.geometry.boundingBox) {
      this.geometry.boundingBox.copy(boundingBox)
    } else {
      this.geometry.boundingBox = boundingBox.clone()
    }
    this.updateBoundingSphereFromBox()
  }

  /**
   * 更新共享帧状态（高斯 pass 和中心点 pass 共用）
   *
   * 同帧复用规则：
   * - 同一帧第一次调用：真正更新
   * - 同一帧后续调用：直接复用
   * - 若第一次 requireStateTexture=false，第二次为 true，允许补生成状态纹理
   *
   * @param renderer - WebGL 渲染器
   * @param camera - 相机
   * @param requireStateTexture - 是否需要状态纹理（中心点 pass 需要）
   * @returns 帧状态，null 表示无效（不可见或无数据）
   */
  updateFrameState(
    renderer: THREE.WebGLRenderer,
    camera: THREE.Camera,
    requireStateTexture: boolean
  ): UnifiedLODFrameState | null {
    const frameId = renderer.info.render.frame

    // 同帧复用：已更新过且不需要补生成状态纹理
    if (this._lastFrameId === frameId) {
      if (!this._frameState.valid) return null
      // 若本次需要状态纹理但上次未生成，则补生成
      if (requireStateTexture && !this._lastFrameHasStateTexture) {
        this._generateStateTexture()
        this._lastFrameHasStateTexture = true
      }
      return this._frameState
    }

    // 新帧：重置缓存
    this._lastFrameId = frameId
    this._lastFrameHasStateTexture = false
    this._frameState.valid = false
    this._frameState.mergedStateTexture = null

    // 检查父节点可见性
    if (this.parent && !this.parent.visible) {
      return null
    }

    // 检查 Layer 是否被禁用
    if (!this.layer.enabled) {
      return null
    }

    // 更新 Director（执行 LOD 评估和排序）
    this.director.updateForRender(this.composition)

    // 获取 Manager
    const manager = this.director.getManager(camera, this.layer)
    if (!manager) {
      this._frameState.manager = null
      return null
    }

    // 检查 WorkBuffer 是否有数据
    const textureSize = manager.textureSize
    if (textureSize === 0 || textureSize === 1) {
      this._frameState.manager = null
      return null
    }

    // 计算可见 splat 数和前向 batched draw instance 数
    const visibleSplatCount = manager.visibleSplatCount
    const batchInstanceCount = getGSplatBatchInstanceCount(visibleSplatCount)

    // 计算视口尺寸
    const size = getRendererViewportDrawingBufferSize(renderer, this._tempViewportSize)

    // 记录 textureSize 变化
    this._lastTextureSize = textureSize

    // 写入帧状态
    this._frameState.manager = manager
    this._frameState.textureSize = textureSize
    this._frameState.visibleSplatCount = visibleSplatCount
    this._frameState.batchInstanceCount = batchInstanceCount
    this._frameState.viewportWidth = size.x
    this._frameState.viewportHeight = size.y
    this._frameState.valid = true

    // 按需生成状态纹理
    if (requireStateTexture) {
      this._generateStateTexture()
      this._lastFrameHasStateTexture = true
    }

    // 标记已初始化
    if (!this._initialized) {
      this._initialized = true
    }

    return this._frameState
  }

  /**
   * 按需生成合并状态纹理
   */
  private _generateStateTexture(): void {
    const manager = this._frameState.manager
    if (!manager) return
    const workBuffer = manager.workBuffer
    if (!workBuffer) return
    const splats = manager.getActiveSplatInfos()
    this._frameState.mergedStateTexture = workBuffer.mergeStateTextures(splats)
  }

  /**
   * 渲染前回调（Three.js 自动调用）
   *
   * 核心逻辑：
   * 1. 调用 updateFrameState() 更新共享帧状态
   * 2. 用结果绑定高斯材质
   * 3. 设置高斯 pass 的实例数和视口参数
   */
  onBeforeRender = (
    renderer: THREE.WebGLRenderer,
    _scene: THREE.Scene,
    camera: THREE.Camera
  ): void => {
    this.ensureRenderableGeometry()
    const state = this.updateFrameState(renderer, camera, false)
    if (!state) {
      this.geometry.instanceCount = 0
      return
    }

    const manager = state.manager!
    if (manager.isWorkBufferRenderable === false) {
      this.geometry.instanceCount = 0
      return
    }

    // 绑定 WorkBuffer 纹理到材质
    this.material.setUnifiedLODTextures(
      manager.colorTexture,
      manager.splatTexture0,
      manager.splatTexture1,
      manager.orderTexture,
      state.textureSize
    )

    // 绑定高质量 SH 纹理（如果启用）
    if (manager.isHighQualitySHActive && manager.shResultTexture) {
      // Unified WorkBuffer 颜色已经在 copy pass 中写入；当前没有 WorkBuffer 布局的 sh_labels。
      this.material.setHighQualitySHTexture(manager.shResultTexture, null)
    } else {
      this.material.setHighQualitySHTexture(null)
    }

    // 绑定标量过滤
    const filterConfig = manager.getScalarFilterConfig()
    if (filterConfig?.enabled && manager.scalarTexture) {
      this.material.setScalarTexture(manager.scalarTexture)
      this.material.setScalarFilter(true, filterConfig.min, filterConfig.max)
    } else {
      this.material.setScalarTexture(null)
      this.material.setScalarFilter(false, 0, 1)
    }

    // 更新实例数量和元数据
    this.geometry.instanceCount = state.batchInstanceCount
    this.material.setMetadata(state.visibleSplatCount, state.textureSize, state.textureSize)

    // 更新视口尺寸
    this.material.setViewport(state.viewportWidth, state.viewportHeight)

    // 更新相机参数
    this.material.setCameraParams(camera)

    // WorkBuffer 中心位于 asset-local，裁切 packet 使用完整 matrixWorld 转到世界裁切空间。
    this.syncClipPacket(this.matrixWorld)

    // 色彩空间自动配置
    this.material.updateGammaMode(renderer)
  }

  /**
   * 获取当前渲染的 Splat 数量
   */
  get instanceCount(): number {
    return this.geometry.instanceCount
  }

  /**
   * 获取是否已初始化
   */
  get isInitialized(): boolean {
    return this._initialized
  }

  /**
   * 获取上一次使用的纹理大小
   */
  get lastTextureSize(): number {
    return this._lastTextureSize
  }

  /**
   * 获取关联的 Layer
   */
  get associatedLayer(): Layer {
    return this.layer
  }

  // ==================== 调试信息收集 ====================

  /**
   * 收集调试状态信息
   *
   * @returns 调试信息字符串
   *
   * @remarks
   * 用于诊断渲染问题
   */
  collectDebugState(): string {
    const parts: string[] = []

    // Mesh 基本状态
    parts.push(`visible=${this.visible}`)
    parts.push(`initialized=${this._initialized}`)
    parts.push(`instanceCount=${this.geometry.instanceCount}`)
    parts.push(`visibleSplatCount=${this._frameState.visibleSplatCount}`)
    parts.push(`batchInstanceCount=${this._frameState.batchInstanceCount}`)
    parts.push(`lastTexSize=${this._lastTextureSize}`)
    parts.push(`inScene=${this.parent !== null}`)
    parts.push(`frustumCulled=${this.frustumCulled}`)
    parts.push(`renderOrder=${this.renderOrder}`)

    // Layer 状态
    parts.push(`layer.id=${this.layer.id}`)
    parts.push(`layer.enabled=${this.layer.enabled}`)
    parts.push(`layer.placements=${this.layer.gsplatPlacements.length}`)

    // 材质状态
    const mat = this.material
    parts.push(`mat.visible=${mat.visible}`)
    parts.push(`mat.transparent=${mat.transparent}`)

    // 检查纹理绑定（以材质 uniforms 为准）
    const uniforms = mat.uniforms
    const hasColorTex = uniforms.splatColor?.value !== null
    const hasSplat0Tex = uniforms.transformA?.value !== null
    const hasSplat1Tex = uniforms.transformB?.value !== null
    const hasOrderTex = uniforms.splatOrder?.value !== null
    parts.push(`texBound=[color=${hasColorTex},splat0=${hasSplat0Tex},order=${hasOrderTex}]`)
    parts.push(`texBoundB=${hasSplat1Tex}`)

    return parts.join(', ')
  }

  /**
   * 销毁 Mesh
   */
  setShaderEffect(effect: GSplatShaderEffectLike | null): boolean {
    if (this._shaderEffect === effect) {
      return effect ? effect.enable(this.material) : true
    }

    this._shaderEffect?.disable()
    this._shaderEffect = effect

    if (!effect) {
      this.material.clearShaderEffect()
      return true
    }

    return effect.enable(this.material)
  }

  clearShaderEffect(): boolean {
    return this.setShaderEffect(null)
  }

  restartShaderEffect(): boolean {
    return this._shaderEffect?.restart() ?? false
  }

  getShaderEffect(): GSplatShaderEffectLike | null {
    return this._shaderEffect
  }

  updateShaderEffect(delta: number): void {
    this._shaderEffect?.update(delta)
  }

  /** 设置当前 unified LOD mesh 使用的 polygon/Box 裁切状态。 */
  setClipState(state: GSplatClipState): void {
    if (this._clipState?.version === state.version) {
      return
    }

    this._clipState = state
    this._clipPacketVersion = -1
    for (const placement of this.layer.gsplatPlacements) {
      placement.clipState = state
    }
    const managers = this.director.getAllManagers?.() ?? []
    for (const manager of managers) {
      for (const placement of this.layer.gsplatPlacements) {
        manager.setClipStateForPlacement(placement, state)
      }
    }
    this.updateMatrixWorld(true)
    this.syncClipPacket(this.matrixWorld)
  }

  private ensureRenderableGeometry(): void {
    const index = this.geometry.index
    const position = this.geometry.getAttribute('position')
    const uv = this.geometry.getAttribute('uv')
    if (
      index?.count === GSPLAT_BATCH_INDEX_COUNT &&
      position?.count === GSPLAT_BATCH_VERTEX_COUNT &&
      uv?.count === GSPLAT_BATCH_VERTEX_COUNT
    ) {
      return
    }
    setBatchedSplatQuadGeometry(this.geometry)
  }

  /** 返回当前裁切状态，未同步时为 null。 */
  getClipState(): GSplatClipState | null {
    return this._clipState
  }

  /** 同步当前资产世界矩阵后返回 gaussian/centers pass 共享的裁切 packet。 */
  getCurrentClipPacket(): GSplatClipPacket | null {
    this.updateMatrixWorld(true)
    this.syncClipPacket(this.matrixWorld)
    return this.material.getClipPacket()
  }

  setWorkBufferModifier(modifier: GSplatWorkBufferModifier | null): boolean {
    const compiled = compileGSplatWorkBufferModifier(modifier)
    const sameModifier =
      this._workBufferModifier?.hash === compiled?.hash &&
      this._workBufferModifier?.code === compiled?.code

    this._workBufferModifier = compiled
    this.material.setWorkBufferModifier(compiled)
    this.applyWorkBufferModifierToPlacements(compiled)

    if (!sameModifier) {
      this.markManagersWorkBufferDirty()
    }

    return this.layer.gsplatPlacements.length > 0
  }

  setWorkBufferUpdateMode(mode: GSplatWorkBufferUpdateMode): boolean {
    if (!UnifiedLODMesh.isWorkBufferUpdateMode(mode)) {
      return false
    }

    this._workBufferUpdateMode = mode
    for (const placement of this.layer.gsplatPlacements) {
      placement.workBufferUpdateMode = mode
    }

    let appliedToManager = false
    for (const manager of this.director.getAllManagers()) {
      for (const placement of this.layer.gsplatPlacements) {
        appliedToManager =
          manager.setWorkBufferUpdateModeForPlacement(placement, mode) || appliedToManager
      }
    }

    if (!appliedToManager && mode !== GSPLAT_WORKBUFFER_UPDATE_AUTO) {
      this.markManagersWorkBufferDirty()
    }

    return this.layer.gsplatPlacements.length > 0
  }

  getWorkBufferUpdateMode(): GSplatWorkBufferUpdateMode {
    return this._workBufferUpdateMode
  }

  private applyWorkBufferModifierToPlacements(
    modifier: CompiledGSplatWorkBufferModifier | null
  ): void {
    for (const placement of this.layer.gsplatPlacements) {
      placement.workBufferModifier = modifier
    }

    for (const manager of this.director.getAllManagers()) {
      for (const placement of this.layer.gsplatPlacements) {
        manager.setWorkBufferModifierForPlacement(placement, modifier)
      }
    }
  }

  private markManagersWorkBufferDirty(): void {
    for (const manager of this.director.getAllManagers()) {
      manager.markWorkBufferDirty()
    }
  }

  private syncClipPacket(matrixWorld: THREE.Matrix4): void {
    const state = this._clipState
    if (state && this._clipPacketVersion === state.version) {
      if (this._clipPacketMatrix.equals(matrixWorld)) {
        return
      }

      const currentPacket = this.material.getClipPacket()
      if (currentPacket) {
        updateGSplatClipPacketMatrix(currentPacket, state, matrixWorld)
        this._clipPacketMatrix.copy(matrixWorld)
        return
      }
    }

    const packet = state ? createGSplatClipPacket(state, matrixWorld) : null
    this.material.setClipPacket(packet, packet !== null)
    this._clipPacketVersion = state?.version ?? -1
    this._clipPacketMatrix.copy(matrixWorld)
  }

  private static isWorkBufferUpdateMode(mode: string): mode is GSplatWorkBufferUpdateMode {
    return (
      mode === GSPLAT_WORKBUFFER_UPDATE_AUTO ||
      mode === GSPLAT_WORKBUFFER_UPDATE_ONCE ||
      mode === GSPLAT_WORKBUFFER_UPDATE_ALWAYS
    )
  }

  dispose(): void {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this._shaderEffect?.destroy()
    this._shaderEffect = null
    this.geometry.dispose()
    this.material.dispose()
  }
}
